import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Clock, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { listTickets } from "../api/department";
import "../styles/department-base.css";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "ticket_assigned", label: "Ticket Assigned" },
  { key: "in_progress", label: "In Progress" },
  { key: "closed", label: "Closed" },
  { key: "overdue", label: "Overdue" },
];

const PRIORITY_STYLES = {
  urgent: "dept-badge--red",
  high: "dept-badge--orange",
  medium: "dept-badge--yellow",
  low: "dept-badge--gray",
};

const STATUS_STYLES = {
  open: "dept-badge--blue",
  ticket_assigned: "dept-badge--purple",
  in_progress: "dept-badge--purple",
  closed: "dept-badge--green",
  triaged: "dept-badge--teal",
};

export default function DepartmentTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (activeTab !== "all") params.status = activeTab;
      if (searchQuery.trim()) params.query = searchQuery.trim();

      const response = await listTickets(params);
      const data = response.data;
      setTickets(data.results || []);
      setTotal(data.meta?.total || 0);
      setTotalPages(Math.ceil((data.meta?.total || 0) / pageSize));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, searchQuery]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery]);

  const openTicket = (ticket) => {
    navigate(`/departments/ticket-detail?id=${ticket.id}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="dept-page">
      <div className="dept-page__header">
        <h1>My Assigned Tickets</h1>
        <span className="dept-text-muted">{total} ticket{total !== 1 ? "s" : ""}</span>
      </div>

      <div className="dept-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`dept-tab ${activeTab === tab.key ? "dept-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="dept-search-bar">
        <Search size={16} className="dept-search-icon" />
        <input
          type="text"
          placeholder="Search tickets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="dept-search-input"
        />
      </div>

      {error && (
        <div className="dept-alert dept-alert--error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="dept-loading">
          <Loader2 size={24} className="dept-spin" />
          <span>Loading tickets...</span>
        </div>
      ) : tickets.length === 0 ? (
        <div className="dept-empty">
          <p>No tickets found.</p>
        </div>
      ) : (
        <>
          <div className="dept-table-wrapper">
            <table className="dept-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Member</th>
                  <th>Last Update</th>
                  <th>SLA</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => {
                  const isOverdue = ticket.slaDeadline && new Date(ticket.slaDeadline) < new Date() && ["open", "ticket_assigned", "in_progress"].includes(ticket.status);
                  return (
                    <tr
                      key={ticket.id}
                      className={`dept-table__row ${isOverdue ? "dept-table__row--overdue" : ""}`}
                      onClick={() => openTicket(ticket)}
                    >
                      <td><code className="dept-ticket-no">{ticket.ticketNo}</code></td>
                      <td className="dept-table__subject">{ticket.subject}</td>
                      <td><span className="dept-badge dept-badge--outline">{ticket.category}</span></td>
                      <td>
                        <span className={`dept-badge ${PRIORITY_STYLES[ticket.priority] || ""}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`dept-badge ${STATUS_STYLES[ticket.status] || ""}`}>
                          {ticket.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </td>
                      <td>{ticket.user?.name || ticket.user?.email || "-"}</td>
                      <td className="dept-text-muted">{ticket.timeAgo || formatDate(ticket.updatedAt)}</td>
                      <td>
                        {ticket.slaDeadline ? (
                          <span className={`dept-sla ${isOverdue ? "dept-sla--overdue" : ""}`}>
                            <Clock size={12} />
                            {formatDate(ticket.slaDeadline)}
                          </span>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="dept-pagination">
              <button
                type="button"
                className="dept-btn dept-btn--ghost dept-btn--sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <span className="dept-text-muted">Page {page} of {totalPages}</span>
              <button
                type="button"
                className="dept-btn dept-btn--ghost dept-btn--sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
